/**
 * "Once per visit" for splash messages (ORDR-164).
 *
 * sessionStorage, keyed by level and node: a visit is this tab, so a diner who
 * comes back tomorrow sees today's message again, and two brands in two tabs
 * never answer for each other. EVERY storage touch is wrapped: a browser that
 * blocks storage (private mode, a strict policy) must still show the splash
 * and still let the diner past it. Without storage the store remembers in
 * memory for this mount only, so the worst case is seeing a message again
 * after a reload, never being stuck behind it.
 */

import type { SplashLevel } from "./splashApi.js";

export interface SplashSeenStore {
  seen(level: SplashLevel, nodeId: string): boolean;
  mark(level: SplashLevel, nodeId: string): void;
}

const PREFIX = "paio-ordering.splash-seen";

function storageOrNull(storage: () => Storage | null): Storage | null {
  try {
    return storage();
  } catch {
    return null;
  }
}

export function createSplashSeenStore(storage: () => Storage | null): SplashSeenStore {
  const remembered = new Set<string>();
  const keyOf = (level: SplashLevel, nodeId: string): string => `${PREFIX}:${level}:${nodeId.toLowerCase()}`;
  return {
    seen(level, nodeId) {
      const key = keyOf(level, nodeId);
      if (remembered.has(key)) {
        return true;
      }
      try {
        return storageOrNull(storage)?.getItem(key) === "1";
      } catch {
        return false;
      }
    },
    mark(level, nodeId) {
      const key = keyOf(level, nodeId);
      remembered.add(key);
      try {
        storageOrNull(storage)?.setItem(key, "1");
      } catch {
        // Blocked or full: the in-memory mark above still lets the diner past.
      }
    },
  };
}

/** This tab's sessionStorage, or `null` where reading it throws. */
export function browserSessionStorage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
}
